import type { CheckoutState } from '../../state';
import {
    CalendarType,
    DateApiResponse,
} from '../../state/infiniteCalendarSeries';
import { getCurrentTimezone, getSeriesInfiniteCalendarFF } from '../app';
import { getEventTimezone, getIsEventOnline } from '../event';
import { getNamespaceValue } from '../utils/selector';

export const getActiveSection = (state: CheckoutState) =>
    state.infiniteCalendarSeries.activeSection;

export const getParentEventInfo = (state: CheckoutState) =>
    state.infiniteCalendarSeries.parentEventInfo;

export const getScrollCalendarData = (state: CheckoutState) =>
    state.infiniteCalendarSeries.scrollCalendarData;

export const getCompactCalendarData = (state: CheckoutState) =>
    state.infiniteCalendarSeries.compactCalendarData;

export const getCalendarType = (state: CheckoutState) =>
    state.infiniteCalendarSeries.calendarType;

export const getCalendarDates = (state: CheckoutState) =>
    state.infiniteCalendarSeries.calendarDates;

export const getTimeSlots = (state: CheckoutState) =>
    state.infiniteCalendarSeries.timeSlots;

export const getIsTimeSectionLoading = (state: CheckoutState) =>
    state.infiniteCalendarSeries.isTimeSectionLoading;

export const getIsTicketSectionLoading = (state: CheckoutState) =>
    state.infiniteCalendarSeries.isTicketSectionLoading;

export const getShouldShowTimeError = (state: CheckoutState) =>
    state.infiniteCalendarSeries.shouldShowTimeError;

export const getShouldShowTicketError = (state: CheckoutState) =>
    state.infiniteCalendarSeries.shouldShowTicketError;

export const getSelectedDate = (state: CheckoutState) =>
    state.infiniteCalendarSeries.selectedDate;

export const getSelectedTime = (state: CheckoutState) =>
    state.infiniteCalendarSeries.selectedTime;

export const getSelectedSessionId = (state: CheckoutState) =>
    state.infiniteCalendarSeries.selectedSessionId;

export const getTimezone = (state: CheckoutState) =>
    getIsEventOnline(state)
        ? getCurrentTimezone(state) || getEventTimezone(state)
        : getEventTimezone(state);

export const getEventInfoCardTimezone = (
    state: CheckoutState,
    useClientTimezone: boolean,
) =>
    getIsEventOnline(state)
        ? getCurrentTimezone(state) ||
          (useClientTimezone
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : getEventTimezone(state))
        : getEventTimezone(state);

export const isCompactCalendar = (calendarType: CalendarType | null) =>
    calendarType === CalendarType.compactWithSession ||
    calendarType === CalendarType.compactWithoutSession;

export const hasParentEventInfoInInfiniteCalendar = (state: CheckoutState) =>
    !!getNamespaceValue(state.infiniteCalendarSeries, 'parentEventInfo');

export const getCalendarDataFromServerContext = (
    state: CheckoutState,
): DateApiResponse | null => {
    const calendarType = getCalendarType(state);
    const eventDates = getCalendarDates(state);

    if (!calendarType || !eventDates) {
        return null;
    }

    return {
        calendarType,
        eventDates,
    };
};

export const getFirstEventDateString = (state: CheckoutState) => {
    const calendarDates = getCalendarDates(state);
    return calendarDates?.[0]?.date || '';
};

export const getFirstSessionStartDateTime = (state: CheckoutState) => {
    const calendarDates = getCalendarDates(state);
    return calendarDates?.[0]?.sessions?.[0]?.startDatetime || '';
};

/**
 * This is primarily used in the routes files (initialize.js, routes.ts) to check if the Series Infinite Calendar experiment is enabled
 * and if the parent event info is present in the state.
 * By checking both conditions, we ensure that when the experiment is active and navigation occurs
 * for non-timed entry events, it doesn't break the navigation.
 */
export const isSeriesInfiniteCalendar = (state: CheckoutState) =>
    getSeriesInfiniteCalendarFF(state) &&
    hasParentEventInfoInInfiniteCalendar(state);
